<script setup>
import { Head, router, usePage } from '@inertiajs/vue3'
import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()
const page = usePage()
const accepted = reactive({})
const processing = reactive({})

const submitConsent = (type) => {
  processing[type] = true

  router.post(
    page.props.submitUrl,
    {
      consent_type: type,
      accepted: accepted[type] ?? false,
    },
    {
      preserveScroll: true,
      onFinish: () => {
        processing[type] = false
      },
    },
  )
}

const consentLabel = (type) => t(`consentTypes.${type}`)
const formatVersionDate = (date) => new Intl.DateTimeFormat(locale.value).format(new Date(date))
const loggedInAs = page.props.auth?.user?.email ?? ''
</script>

<template>
  <Head :title="t('title')" />

  <AuthCard>
    <template #title>{{ t('title') }}</template>
    <template #subtitle>{{ t('subtitle') }}</template>
    <template #content>
      <div class="flex flex-col gap-4">
        <div
          v-for="consent in page.props.pendingConsents"
          :key="`${consent.type}-${consent.version}`"
          class="border-surface-200 rounded-xl border p-4">
          <div class="flex flex-col gap-3">
            <div class="flex flex-col gap-1">
              <div class="text-surface-900 font-medium">{{ consentLabel(consent.type) }}</div>
              <div class="text-surface-600 text-sm">
                {{ t('versionDate', { date: formatVersionDate(consent.version_date) }) }}
              </div>
              <a :href="consent.url" target="_blank" class="text-action text-sm font-medium">
                {{ t('open', { label: consentLabel(consent.type) }) }}
              </a>
            </div>

            <label class="flex items-center gap-3 text-sm">
              <Checkbox v-model="accepted[consent.type]" :input-id="`consent-${consent.type}`" binary />
              <span>{{ t('confirm', { label: consentLabel(consent.type) }) }}</span>
            </label>

            <div class="flex justify-end">
              <Button
                type="button"
                :label="t('submit')"
                :disabled="!(accepted[consent.type] ?? false)"
                :loading="processing[consent.type] ?? false"
                @click="submitConsent(consent.type)" />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <Divider />
      <div class="flex items-center justify-between gap-4">
        <div class="text-surface-600 text-sm">
          {{ t('loggedInAs', { name: loggedInAs }) }}
        </div>
        <Button v-slot="slotProps" variant="link" as-child>
          <Link :href="page.props.logoutUrl" method="post" as="button" replace v-bind="slotProps">
            {{ t('logout') }}
          </Link>
        </Button>
      </div>
    </template>
  </AuthCard>
</template>

<i18n>
{
    "en": {
        "title": "Confirm legal documents",
        "subtitle": "Please confirm each required document to continue.",
        "versionDate": "Valid from {date}",
        "open": "Open {label}",
        "confirm": "I have read and accept {label}.",
        "submit": "Confirm",
        "loggedInAs": "Signed in: {name}",
        "logout": "Logout",
        "consentTypes": {
            "terms": "Terms of Service",
            "privacy": "Privacy Policy"
        }
    },
    "de": {
        "title": "Rechtliche Dokumente bestätigen",
        "subtitle": "Bitte bestätige jedes erforderliche Dokument, damit Du fortfahren kannst.",
        "versionDate": "Gültig ab {date}",
        "open": "{label} öffnen",
        "confirm": "Ich habe {label} gelesen und akzeptiere sie.",
        "submit": "Bestätigen",
        "loggedInAs": "Angemeldet: {name}",
        "logout": "Abmelden",
        "consentTypes": {
            "terms": "AGB",
            "privacy": "Datenschutzhinweise"
        }
    }
}
</i18n>
